import { useState } from 'react';
import { TextField, Typography } from '@mui/material';
import BackpackRounded from '@mui/icons-material/BackpackRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import RoundIconButton from 'components/common/RoundIconButton/RoundIconButton';
import TSCard from 'components/common/TSCard/TSCard';
import TSButton from 'components/common/Button/Button';
import { gearSummary, isEssentialNotTaking } from 'utils/gearList';
import './GearChecklist.module.scss';

// A Gear List as a checklist: each item ({ id, name, essential, status,
// walker_added }) can be marked taking (✓) or not taking (✗), or left
// unanswered, and the walker can add their own. Tapping the chosen answer
// again clears it. An Essential item marked not taking shows a warning, but never
// blocks anything (ADR-0017). onMarkEssentials marks every Essential item
// taking at once, so the walker needn't tap through them. onRemove, if given,
// lets the walker take back an item of their own. Where the answers are saved
// is the caller's concern.
const GearChecklist = ({
  items,
  onMark,
  onMarkEssentials,
  onAdd,
  onRemove,
  editable = true,
  isAdding = false,
}) => {
  const [newItem, setNewItem] = useState('');
  const { answered } = gearSummary(items);
  const essentials = items.filter((item) => item.essential);

  const handleAdd = async () => {
    if (await onAdd(newItem.trim())) setNewItem('');
  };

  return (
    <TSCard icon={<BackpackRounded />} title="Gear List">
      <Typography variant="body1" color="text.secondary">
        {answered} of {items.length} answered. This is guidance only: it never
        stops you starting.
      </Typography>
      {editable && essentials.length > 0 && (
        <div>
          <TSButton
            btnText="Taking all Essential items"
            btnVariant="outlined"
            btnDisable={essentials.every((item) => item.status === 'TAKING')}
            onClickFunc={onMarkEssentials}
          />
        </div>
      )}
      <ul className="ts-gear">
        {items.map((item) => (
          <li
            key={item.id}
            className={`ts-gear__item ${editable ? '' : 'ts-gear__item--locked'}`}
          >
            <span className="ts-gear__name">
              {item.name}
              {item.essential && <span className="ts-tag">Essential</span>}
            </span>
            {item.walker_added && onRemove ? (
              <TSButton
                btnText="Remove"
                btnVariant="outlined"
                btnSize="small"
                onClickFunc={() => onRemove(item)}
              />
            ) : (
              <div
                className="ts-gear__answer"
                role="group"
                aria-label={`Are you taking ${item.name}?`}
              >
                {[
                  ['TAKING', 'Taking', CheckRounded, 'success'],
                  ['NOT_TAKING', 'Not taking', CloseRounded, 'danger'],
                ].map(([status, label, Icon, tone]) => (
                  <RoundIconButton
                    key={status}
                    icon={<Icon />}
                    label={label}
                    showLabel
                    tone={tone}
                    pressed={item.status === status}
                    disabled={!editable}
                    onClick={() =>
                      onMark(
                        item,
                        item.status === status ? 'UNANSWERED' : status
                      )
                    }
                  />
                ))}
              </div>
            )}
            {isEssentialNotTaking(item) && (
              <p className="ts-gear__warning">
                <WarningAmberRounded aria-hidden="true" />
                This is Essential for this Trail. You can still go, but we
                strongly recommend taking it.
              </p>
            )}
          </li>
        ))}
      </ul>
      {editable && (
        <div className="flex flex-wrap gap-2 items-center">
          <TextField
            label="Add your own item"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
          />
          <TSButton
            btnText={isAdding ? 'Adding...' : 'Add'}
            btnVariant="outlined"
            btnDisable={!newItem.trim() || isAdding}
            onClickFunc={handleAdd}
          />
        </div>
      )}
    </TSCard>
  );
};

export default GearChecklist;
